import React, { useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  ipdSemanticSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { addAllergy, removeAllergy } from "../api/emergencyApi";

/**
 * Allergies and adverse reactions on the visit (ticket 15).
 *
 * The list itself is also surfaced as a banner at the top of the visit — this
 * card is where entries are added and removed. A severity is optional: it is
 * not always known at the point someone volunteers "penicillin gives me a
 * rash", and refusing the allergy for want of a severity would be the wrong
 * trade.
 */

const SEVERITY = {
  severe: { label: "Severe", variant: "error" },
  moderate: { label: "Moderate", variant: "warning" },
  mild: { label: "Mild", variant: "info" },
};

const SEVERITY_OPTIONS = [
  { value: "severe", label: "Severe" },
  { value: "moderate", label: "Moderate" },
  { value: "mild", label: "Mild" },
];

function SeverityChip({ severity }) {
  const m = SEVERITY[severity];
  if (!m) return null;
  return (
    <Box
      component="span"
      sx={{
        ...ipdSemanticSx(m.variant),
        px: 0.9,
        py: 0.2,
        borderRadius: `${IPD_SHAPE.radiusSm}px`,
        fontSize: 11,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </Box>
  );
}

export default function AllergiesCard({ visit, onChanged, disabled }) {
  const allergies = visit.allergies || [];

  const [allergen, setAllergen] = useState("");
  const [reaction, setReaction] = useState("");
  const [severity, setSeverity] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    run(async () => {
      await addAllergy(visit.id, {
        allergen: allergen.trim(),
        reaction: reaction.trim(),
        severity,
      });
      setAllergen("");
      setReaction("");
      setSeverity("");
    });

  const canAdd = Boolean(allergen.trim()) && !busy;

  return (
    <IpdSectionCard
      title="Allergies & adverse reactions"
      subtitle="What could hurt this patient — kept visible, not buried"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {allergies.length === 0 ? (
        <Box
          sx={{
            px: 2,
            py: 1.75,
            mb: 2,
            border: `1px dashed ${IPD_COLORS.borderHover}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>No allergies recorded</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
            Ask the patient. Anything they react to goes here so it is impossible to miss.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {allergies.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                px: 1.75,
                py: 1.25,
                border: `1px solid ${IPD_COLORS.error.border}`,
                bgcolor: IPD_COLORS.error.bg,
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.error.text }}>
                    {a.allergen}
                  </Typography>
                  <SeverityChip severity={a.severity} />
                </Box>
                {a.reaction && (
                  <Typography sx={{ mt: 0.3, fontSize: 13, color: IPD_COLORS.textPrimary }}>
                    {a.reaction}
                  </Typography>
                )}
                <Typography sx={{ mt: 0.3, fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  {formatDateTime(a.recorded_at)}
                  {a.recorded_by_name ? ` · ${a.recorded_by_name}` : ""}
                </Typography>
              </Box>
              {!disabled && (
                <Tooltip title="Remove this allergy">
                  <IconButton
                    size="small"
                    disabled={busy}
                    aria-label={`Remove allergy ${a.allergen}`}
                    onClick={() => run(() => removeAllergy(visit.id, a.id))}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!disabled && (
        <Box
          sx={{
            p: 1.75,
            bgcolor: IPD_COLORS.pageBg,
            border: `1px solid ${IPD_COLORS.border}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ ...ipdLabelSx, mb: 1.25 }}>Record an allergy</Typography>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr" }, gap: 1.5 }}>
              <TextField
                value={allergen}
                onChange={(e) => setAllergen(e.target.value)}
                label="Allergen"
                placeholder="Penicillin, peanuts, latex…"
                size="small"
                required
                sx={ipdElevatedInputSx}
              />
              <TextField
                select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                label="Severity"
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={ipdElevatedInputSx}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                helperText="Optional — leave blank if not known"
              >
                <MenuItem value="">Not known</MenuItem>
                {SEVERITY_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              value={reaction}
              onChange={(e) => setReaction(e.target.value)}
              label="Reaction"
              placeholder="Rash, swelling, anaphylaxis…"
              size="small"
              fullWidth
              sx={ipdElevatedInputSx}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IpdButton startIcon={<AddIcon />} onClick={submit} disabled={!canAdd} loading={busy}>
                Record allergy
              </IpdButton>
            </Box>
          </Box>
        </Box>
      )}

      {allergies.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: IPD_COLORS.textMuted }} />
          <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
            Shown as a banner at the top of the visit so it is seen first.
          </Typography>
        </Box>
      )}
    </IpdSectionCard>
  );
}
